import cls from "./LearnDashboardSidebar.module.scss";
import React, { FC, useEffect, useState } from "react";
import { classNames } from "../../../../shared/lib/className/className";
import { CourseMaterial, Item } from "../../../../types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import arrow from "../../../../assets/icons/arrow.svg";
import DownloadIcon from "@mui/icons-material/Download";
import DropdownButton from "../../../LearnPage/ui/LearnPageSidebar/LearnPageSidebar";

interface LearnDashboardSidebarProps {
  id?: string;
  collapsed: any;
  setCollapsed: (collapsed: boolean) => void;
  className?: string;
  courseMaterial: CourseMaterial | undefined;
  onItemSelect: (
    contentType: "article" | "video" | "slide" | "test" | "task",
    contentData: any
  ) => void;
  passedPart?: any[];
}

export const LearnDashboardSidebar: FC<LearnDashboardSidebarProps> = ({
  className,
  courseMaterial,
  onItemSelect,
  setCollapsed,
  collapsed,
  passedPart,
}) => {
  const isMobile = useMediaQuery("(max-width:1000px)");
  const [expanded, setExpanded] = useState<string | false>("panel0");
  // eslint-disable-next-line
  const [lastPassedSection, setLastPassedSection] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (passedPart && passedPart.length > 0 && courseMaterial) {
      const lastPassedLesson = passedPart[passedPart.length - 1];
      const foundSectionIndex = courseMaterial.data.findIndex((section) =>
        section.items.some(
          (item: { lecture_name: any }) =>
            item.lecture_name === lastPassedLesson.lecture_name
        )
      );
      setLastPassedSection(foundSectionIndex);
      if (foundSectionIndex !== -1) {
        setExpanded(`panel${foundSectionIndex}`);
      }
    }
  }, [passedPart, courseMaterial]);

  // eslint-disable-next-line
  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const onToggle = () => {
    setCollapsed(!collapsed);
  };

  return (
    <>
      <div
        className={classNames(
          cls.sidebar,
          { [cls.collapsed]: collapsed },
          className ? [className] : []
        )}>
        {collapsed ? (
          ""
        ) : (
          <Typography
            sx={{ fontSize: "20px", fontWeight: 800, marginBottom: "16px" }}>
            Материалы курса
          </Typography>
        )}
        {!isMobile && (
          <Button
            onClick={onToggle}
            sx={{
              width: "100%",
              borderRadius: "8px",
              backgroundColor: null,
              color: "gray",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 700,
              "&:hover": {
                borderColor: "gray",
              },
              "& .MuiButton-startIcon": {
                rotate: collapsed ? "180deg" : "0deg",
              },
            }}
            startIcon={<img src={arrow} alt="arrow" />}>
            {collapsed ? "" : "Свернуть"}
          </Button>
        )}
        {!collapsed &&
          courseMaterial?.data.map((part, index) => (
            <Accordion
              key={part.id}
              expanded={expanded === `panel${index}`}
              onChange={handleAccordionChange(`panel${index}`)}
              sx={{
                boxShadow: "none",
                borderRadius: "8px",
                "&:not(:last-child)": {
                  margin: "12px 0 12px 0",
                },
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  borderRadius: "8px",
                },
                ...(expanded === `panel${index}` && {
                  border: "2px solid #0573E4",
                }),
                "& .MuiAccordionSummary-root": {
                  borderBottom:
                    expanded === `panel${index}`
                      ? "1px solid rgba(0, 0, 0, .125)"
                      : "none",
                  borderRadius: "8px",
                },
                "& .MuiAccordionDetails-root": {
                  borderTop: "none",
                },
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    flexDirection: "column",
                  },
                }}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 800,
                  }}>{`Раздел ${index + 1}: ${part.name}`}</Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "gray",
                  }}>{`0 | ${part.items.length} | 13 мин`}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List
                  sx={{
                    padding: 0,
                    "& .MuiButtonBase-root": {
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                    },
                  }}>
                  {part.items.map((item: Item, itemIndex: number) => {
                    if (
                      item.slides &&
                      item.slides_file_extension &&
                      item.slides_file_name
                    ) {
                      const downloadUrl = item.slides;
                      let fileName = item.slides_file_name;
                      fileName = fileName
                        .replace(/[|&;$%@"<>()+,]/g, "")
                        .replace(/\s+/g, "_");
                      fileName += "." + item.slides_file_extension;

                      return (
                        <ListItem
                          sx={{
                            padding: "5px 0",
                            "&:not(:first-of-type)": {
                              borderTop: "1px solid #e0e0e0",
                            },
                          }}
                          key={itemIndex}
                          button
                          onClick={() => {
                            let contentType:
                              | "article"
                              | "video"
                              | "slide"
                              | "test"
                              | "task";
                            if (
                              item.video &&
                              item.video_name &&
                              item.video_extension
                            ) {
                              contentType = "video";
                            } else if (
                              item.slides &&
                              item.slides_file_extension &&
                              item.slides_file_name
                            ) {
                              contentType = "slide";
                            } else if (item.test_name && item.questions) {
                              contentType = "test";
                            } else if (
                              item.exercise_name &&
                              item.instructions
                            ) {
                              contentType = "task";
                            } else {
                              contentType = "article";
                            }
                            onItemSelect(contentType, item);
                          }}>
                          <Checkbox
                            sx={{
                              padding: "0 8px 0 0",
                            }}
                            checked={
                              !!passedPart?.find((el) => {
                                if (
                                  (el.exercise_name === item.exercise_name &&
                                    item.exercise_name) ||
                                  (el.task_name === item.task_name &&
                                    item.task_name) ||
                                  (el.test_name === item.test_name &&
                                    item.test_name)
                                )
                                  return true;
                                let ans = true;
                                for (const [key, value] of Object.entries(
                                  item
                                )) {
                                  if (el[key] !== value) {
                                    ans = false;
                                  }
                                }
                                return ans;
                              })
                            }
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                              }}
                              variant="body2">
                              {item.lecture_name}
                            </Typography>
                            <Box>
                              <a
                                href={downloadUrl}
                                download={fileName}
                                key={itemIndex}
                                className={cls.buttonDownload}>
                                <DownloadIcon className={cls.downloadIcon} />
                                <Typography
                                  sx={{ fontWeight: 700, fontSize: "14px" }}>
                                  Слайды
                                </Typography>
                              </a>
                            </Box>
                          </Box>
                        </ListItem>
                      );
                    } else if (item.file) {
                      return (
                        <ListItem
                          sx={{
                            padding: "5px 0",
                            "&:not(:first-of-type)": {
                              borderTop: "1px solid #e0e0e0",
                            },
                          }}
                          key={itemIndex}
                          button
                          onClick={() => {
                            let contentType:
                              | "article"
                              | "video"
                              | "slide"
                              | "test"
                              | "task";
                            if (
                              item.video &&
                              item.video_name &&
                              item.video_extension
                            ) {
                              contentType = "video";
                            } else if (
                              item.slides &&
                              item.slides_file_extension &&
                              item.slides_file_name
                            ) {
                              contentType = "slide";
                            } else if (item.test_name && item.questions) {
                              contentType = "test";
                            } else if (
                              item.exercise_name &&
                              item.instructions
                            ) {
                              contentType = "task";
                            } else {
                              contentType = "article";
                            }
                            onItemSelect(contentType, item);
                          }}>
                          <Checkbox
                            sx={{
                              padding: "0 8px 0 0",
                            }}
                            checked={
                              !!passedPart?.find((el) => {
                                if (
                                  (el.exercise_name === item.exercise_name &&
                                    item.exercise_name) ||
                                  (el.task_name === item.task_name &&
                                    item.task_name) ||
                                  (el.test_name === item.test_name &&
                                    item.test_name)
                                )
                                  return true;
                                let ans = true;
                                for (const [key, value] of Object.entries(
                                  item
                                )) {
                                  if (el[key] !== value) {
                                    ans = false;
                                  }
                                }
                                return ans;
                              })
                            }
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                            }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                              }}
                              variant="body2">
                              {item.exercise_name}
                            </Typography>
                            <Box>
                              <DropdownButton
                                link={item.file}
                                fileName={item.file_name}
                              />
                            </Box>
                          </Box>
                        </ListItem>
                      );
                    } else {
                      return (
                        <ListItem
                          sx={{
                            padding: "5px 0",
                            "&:not(:first-of-type)": {
                              borderTop: "1px solid #e0e0e0",
                            },
                          }}
                          key={itemIndex}
                          button
                          onClick={() => {
                            let contentType:
                              | "article"
                              | "video"
                              | "slide"
                              | "test"
                              | "task";
                            if (
                              item.video &&
                              item.video_name &&
                              item.video_extension
                            ) {
                              contentType = "video";
                            } else if (
                              item.slides &&
                              item.slides_file_extension &&
                              item.slides_file_name
                            ) {
                              contentType = "slide";
                            } else if (item.test_name && item.questions) {
                              contentType = "test";
                            } else if (
                              item.exercise_name &&
                              item.instructions
                            ) {
                              contentType = "task";
                            } else {
                              contentType = "article";
                            }
                            onItemSelect(contentType, item);
                          }}>
                          <Checkbox
                            sx={{
                              padding: "0 8px 0 0",
                            }}
                            checked={
                              !!passedPart?.find((el) => {
                                if (
                                  (el.exercise_name === item.exercise_name &&
                                    item.exercise_name) ||
                                  (el.task_name === item.task_name &&
                                    item.task_name) ||
                                  (el.test_name === item.test_name &&
                                    item.test_name)
                                )
                                  return true;
                                let ans = true;
                                for (const [key, value] of Object.entries(
                                  item
                                )) {
                                  if (el[key] !== value) {
                                    ans = false;
                                  }
                                }
                                return ans;
                              })
                            }
                          />
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                              }}
                              variant="body2">
                              {item.lecture_name ||
                                item.test_name ||
                                item.task_name ||
                                item.exercise_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "gray",
                              }}>{`0 | ${part.items.length} | 13 мин`}</Typography>
                          </Box>
                        </ListItem>
                      );
                    }
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
      </div>
    </>
  );
};
